import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { normalizePlanName } from '../lib/subscription-utils';

interface SubscriptionData {
  plan: string;
  status: string;
  currentPeriodEnd: string | null;
  isLoading: boolean;
  error: string | null;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData>({
    plan: 'Lunch',
    status: 'active',
    currentPeriodEnd: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!user || !isSupabaseConfigured || !supabase) {
      setSubscription(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const fetchSubscription = async () => {
      try {
        setSubscription(prev => ({ ...prev, isLoading: true, error: null }));

        // Récupérer le plan depuis user_profiles
        if (!supabase) {
          setSubscription(prev => ({ ...prev, isLoading: false }));
          return;
        }
        
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('subscription_plan')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          console.warn('Erreur lors de la récupération du profil:', profileError);
          // Continuer avec un plan par défaut si le profil n'existe pas encore
        }

        // Récupérer les détails de l'abonnement (optionnel)
        let subscriptionData = null;
        try {
          if (supabase) {
            const { data, error: subscriptionError } = await supabase
              .from('user_subscriptions')
              .select('status, end_date, next_billing_date')
              .eq('user_id', user.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
          
            if (subscriptionError) {
              // Erreur 400/406 peut signifier que la table ou les colonnes n'existent pas ou RLS bloque
              const errorCode = subscriptionError.code || '';
              const errorMessage = String(subscriptionError.message || '');
              const errorDetails = String(subscriptionError.details || '');
              const httpStatus = (subscriptionError as any)?.status || '';
              const isTableMissing = errorCode === 'PGRST400' || 
                                     errorCode === 'PGRST406' || 
                                     errorCode === '42P01' ||
                                     errorCode === '42883' ||
                                     httpStatus === 400 ||
                                     httpStatus === 406 ||
                                     errorMessage.toLowerCase().includes('does not exist') ||
                                     errorMessage.toLowerCase().includes('relation') ||
                                     errorMessage.toLowerCase().includes('column') ||
                                     errorMessage.toLowerCase().includes('undefined table') ||
                                     errorDetails.toLowerCase().includes('does not exist');
              
              if (isTableMissing) {
                // Table ou colonnes n'existent pas - c'est normal, on continue sans
                // Ne pas loguer pour éviter le spam dans la console
                subscriptionData = null;
              } else {
                // Autre type d'erreur - on log seulement si ce n'est pas une erreur de table
                console.warn('Erreur lors de la récupération de user_subscriptions:', subscriptionError);
              }
            } else if (data) {
              subscriptionData = data;
            }
          }
        } catch (err) {
          // Ignorer silencieusement les erreurs liées à user_subscriptions
          subscriptionData = null;
        }

        // ✅ Use centralized normalizePlanName function for consistency
        const normalizedPlan = normalizePlanName(profile?.subscription_plan) || 'Lunch';

        setSubscription({
          plan: normalizedPlan,
          status: subscriptionData?.status || 'active',
          currentPeriodEnd: subscriptionData?.next_billing_date || subscriptionData?.end_date || null,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'abonnement:', error);
        setSubscription(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Erreur inconnue',
        }));
      }
    };

    fetchSubscription();
  }, [user, supabase]);

  const hasAccess = (requiredPlan: string): boolean => {
    const planHierarchy = {
      'Lunch': 0,
      'Boost': 1,
      'Scale': 2,
    };

    const userPlanLevel = planHierarchy[subscription.plan as keyof typeof planHierarchy] || 0;
    const requiredPlanLevel = planHierarchy[requiredPlan as keyof typeof planHierarchy] || 0;

    return userPlanLevel >= requiredPlanLevel;
  };

  const isActive = (): boolean => {
    return subscription.status === 'active' && subscription.plan !== 'Lunch';
  };

  const canAccess = (pageType: 'admin' | 'pro' | 'premium'): boolean => {
    switch (pageType) {
      case 'admin':
        return subscription.plan === 'Scale'; // Seuls les utilisateurs Scale peuvent accéder à l'admin
      case 'pro':
        return hasAccess('Boost');
      case 'premium':
        return hasAccess('Scale');
      default:
        return false;
    }
  };

  return {
    ...subscription,
    hasAccess,
    isActive,
    canAccess,
  };
};

export default useSubscription;